import { useQuery } from "@apollo/client";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GET_FIRMA_AUTOCOMPLETE_QUERY } from "../../api/firma/queries";
import { getKommuneByKommuneKode } from "../../util/kommune";
import FirmaIcon from "../icons/FirmaIcon";
import AutoComplete, { AutoCompleteOnSelectedItemChange } from "./AutoComplete";
import { AutoCompleteItem, FirmaAutoCompleteResponse } from "./interface";
import { startCase } from "lodash";
import {
  FirmaFilterKeys,
  IFirmaSearchFilterSetting,
  QueryFilter,
} from "../../interfaces/filters";
import { f } from "../../util/f";
import { FirmaDtoFilterInput } from "../../interfaces/schema";
import { FirmaFiltersEventContext } from "../../context/FiltersContext";

export type AutoCompleteFirmaResponse = {
  firmaer: {
    edges: {
      node: FirmaAutoCompleteResponse;
    }[];
  };
};

export type AutoCompleteFirmaOnSelectedItem =
  AutoCompleteItem<AutoCompleteFirmaResponse | undefined> | null | undefined;

export default function AutoCompleteFirma({
  includeFirstItem,
  className,
  sizeSmall,
  sizeLarge,
  clearAfterSelection,
  onSelectedItemChange,
  placeholder,
  isInvalidClass,
  onInputBlur,
  focusInputOnMount,
}: {
  includeFirstItem: boolean;
  className?: string;
  isInvalidClass?: string;
  clearAfterSelection?: boolean;
  onSelectedItemChange?: AutoCompleteOnSelectedItemChange<AutoCompleteFirmaResponse>;
  placeholder?: string;
  sizeSmall?: boolean;
  sizeLarge?: boolean;
  onInputBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  focusInputOnMount?: boolean;
}) {
  const updateFilters = useContext(FirmaFiltersEventContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const { data, loading } = useQuery<AutoCompleteFirmaResponse>(
    GET_FIRMA_AUTOCOMPLETE_QUERY({
      q: inputValue,
    }),
    { variables: { q: inputValue }, skip: !inputValue },
  );

  const items =
    !loading && data && data.firmaer.edges.length > 0
      ? data.firmaer.edges.map((e) => {
          const p = e.node;
          const kommune = getKommuneByKommuneKode(p.kommuneNr);
          const item: AutoCompleteItem<AutoCompleteFirmaResponse> = {
            id: p.id,
            header: `${p.navn1}`,
            subheader: `${p.firmaNr}${
              " | " +
              startCase(p.firmatype.toLocaleLowerCase()) +
              `${p.cvrNr ? ` | CVR: ${p.cvrNr}` : ""}`
            }${" | " + (p.aktiv ? "Aktiv" : "Inaktiv")}${
              kommune ? " | " + kommune.navn + " kommune" : ""
            }`,
            icon: <FirmaIcon width={22} />,
          };
          return item;
        })
      : [];

  const defaultOnSelectedItemChange: AutoCompleteOnSelectedItemChange<
    AutoCompleteFirmaResponse
  > = ({ selectedItem }) => {
    if (selectedItem) {
      if (selectedItem.id && selectedItem.id !== "first") {
        navigate(`/firma/${selectedItem.id}`, { replace: false });
      } else {
        const inputs = inputValue?.split(" ").filter((value) => Boolean(value));

        const searchQueryFilter: QueryFilter<
          FirmaFilterKeys,
          IFirmaSearchFilterSetting
        > = {
          filterKey: "search",
          parentFilter: "and",
          filter: f<FirmaDtoFilterInput>()
            .addFilter(
              "and",
              inputs.length
                ? inputs?.map((input) => {
                    const inputNumber = Number(input);
                    const isInputNumber = !isNaN(inputNumber);

                    if (isInputNumber) {
                      return {
                        or: [
                          {
                            firmaNr: {
                              eq: inputNumber,
                            },
                          },
                          {
                            cvrNr: {
                              eq: inputNumber,
                            },
                          },
                        ],
                      };
                    } else {
                      return {
                        navn1: {
                          contains: input,
                        },
                      };
                    }
                  })
                : [],
            )
            .build(),
          settings: {
            query: `Navn indeholder: "${inputValue}"`,
          },
        };
        updateFilters({
          event: "update",
          queryFilter: searchQueryFilter,
        });

        navigate(`/firma`, {
          replace: pathname === "/firma",
        });
      }
    }
  };

  return (
    <AutoComplete<AutoCompleteFirmaResponse>
      className={className}
      isInvalidClass={isInvalidClass}
      sizeSmall={sizeSmall}
      sizeLarge={sizeLarge}
      placeholder={placeholder ?? "Søg på firma ..."}
      items={items}
      itemToString={(item) => {
        if (item?.id !== "first") {
          return (item?.header as string) ?? "";
        } else {
          return inputValue;
        }
      }}
      loading={loading}
      setInputValue={setInputValue}
      onSelectedItemChange={onSelectedItemChange ?? defaultOnSelectedItemChange}
      includeFirstItem={includeFirstItem}
      clearAfterSelection={clearAfterSelection}
      onInputBlur={onInputBlur}
      focusInputOnMount={focusInputOnMount}
    />
  );
}
